import { useState } from 'react';
import { Dialog, Icon } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import styled from 'styled-components';
import { CancelAction, SubmitAction } from 'components/ListVehicles/components/CreateVehicleDialog/ui';

/* ─── Styled components ─────────────────────────────────────────────────── */

const StyledDialog = styled(Dialog)`
  .unaward-fee-paper {
    margin: auto 13px;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    width: 100%;
    max-width: 510px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 32px 28px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 500;
  color: #000;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 120px;
  gap: 12px 16px;
  align-items: start;
`;

const HeaderCell = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #757575;
`;

const RowLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #757575;
  padding-top: 10px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorText = styled.span`
  font-size: 11px;
  color: #f44336;
  margin-top: 2px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 19px;
`;

const inputStyles = `
  background: #f5f5f5;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  color: #212121;
  &:hover {
    border-color: #bdbdbd;
  }
  &:focus {
    border-color: #005a96;
    border-width: 2px;
    padding: 9px 11px;
  }
`;

const StyledTextInput = styled.input`
  ${inputStyles}
`;

const StyledAmountInput = styled(NumberFormat)`
  ${inputStyles}
`;

/* ─── Types ─────────────────────────────────────────────────────────────── */

export type UnawardFeeEntry = { description: string; amount: number };

export interface UnawardFeeDialogProps {
  open: boolean;
  buyerName: string;
  sellerName: string;
  onCancel: () => void;
  onConfirm: (buyerFee: UnawardFeeEntry | null, sellerFee: UnawardFeeEntry | null) => void;
}

/* ─── Component ─────────────────────────────────────────────────────────── */

export const UnawardFeeDialog = ({ open, buyerName, sellerName, onCancel, onConfirm }: UnawardFeeDialogProps) => {
  const [buyerFeeType, setBuyerFeeType] = useState('');
  const [buyerFeeAmount, setBuyerFeeAmount] = useState('');
  const [sellerFeeType, setSellerFeeType] = useState('');
  const [sellerFeeAmount, setSellerFeeAmount] = useState('');
  const [errors, setErrors] = useState<{ buyer?: string; seller?: string }>({});

  const reset = () => {
    setBuyerFeeType('');
    setBuyerFeeAmount('');
    setSellerFeeType('');
    setSellerFeeAmount('');
    setErrors({});
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleConfirm = () => {
    const buyerAmount = parseFloat(buyerFeeAmount) || 0;
    const sellerAmount = parseFloat(sellerFeeAmount) || 0;
    const newErrors: { buyer?: string; seller?: string } = {};

    if (buyerAmount > 0 && !buyerFeeType.trim()) {
      newErrors.buyer = 'Fee type is required';
    }
    if (sellerAmount > 0 && !sellerFeeType.trim()) {
      newErrors.seller = 'Fee type is required';
    }

    if (newErrors.buyer || newErrors.seller) {
      setErrors(newErrors);
      return;
    }

    const buyerFee = buyerAmount > 0 ? { description: buyerFeeType.trim(), amount: buyerAmount } : null;
    const sellerFee = sellerAmount > 0 ? { description: sellerFeeType.trim(), amount: sellerAmount } : null;

    reset();
    onConfirm(buyerFee, sellerFee);
  };

  return (
    <StyledDialog classes={{ paper: 'unaward-fee-paper' }} open={open} onClose={handleCancel}>
      <Wrapper>
        <Header>
          <Title>Do you want to apply fee?</Title>
          <Icon onClick={handleCancel} style={{ cursor: 'pointer', fontSize: 24 }}>
            close
          </Icon>
        </Header>

        <Grid>
          <span />
          <HeaderCell>Fee Type</HeaderCell>
          <HeaderCell>Fee</HeaderCell>

          <RowLabel>Buyer: {buyerName}</RowLabel>
          <FieldWrapper>
            <StyledTextInput
              value={buyerFeeType}
              onChange={e => {
                setBuyerFeeType(e.target.value);
                if (errors.buyer) setErrors(prev => ({ ...prev, buyer: undefined }));
              }}
            />
            {errors.buyer && <ErrorText>{errors.buyer}</ErrorText>}
          </FieldWrapper>
          <FieldWrapper>
            <StyledAmountInput
              prefix="$"
              thousandSeparator
              decimalScale={0}
              allowNegative={false}
              value={buyerFeeAmount}
              onValueChange={({ value }) => setBuyerFeeAmount(value)}
            />
          </FieldWrapper>

          <RowLabel>Seller: {sellerName}</RowLabel>
          <FieldWrapper>
            <StyledTextInput
              value={sellerFeeType}
              onChange={e => {
                setSellerFeeType(e.target.value);
                if (errors.seller) setErrors(prev => ({ ...prev, seller: undefined }));
              }}
            />
            {errors.seller && <ErrorText>{errors.seller}</ErrorText>}
          </FieldWrapper>
          <FieldWrapper>
            <StyledAmountInput
              prefix="$"
              thousandSeparator
              decimalScale={0}
              allowNegative={false}
              value={sellerFeeAmount}
              onValueChange={({ value }) => setSellerFeeAmount(value)}
            />
          </FieldWrapper>
        </Grid>

        <ActionsWrapper>
          <CancelAction fullWidth variant="contained" type="button" onClick={handleCancel}>
            Cancel
          </CancelAction>
          <SubmitAction fullWidth variant="contained" type="button" onClick={handleConfirm}>
            Confirm
          </SubmitAction>
        </ActionsWrapper>
      </Wrapper>
    </StyledDialog>
  );
};
